import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { EntityTypes } from '@interfaces/EntityTypes';
import { UserDropdownDTO } from '@interfaces/user-dropdown-list';
import { IAdminDashboardPrivileges, ISecurityRec, IUserPage } from '@interfaces/userPage';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable, Subject, merge } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import {
  CacheBuster,
  Cacheable,
  globalCacheBusterNotifier,
} from 'ts-cacheable';
import { environment } from '../../environments/environment';
import { DSRESTClient } from '../shared/communication-objects/restclient.service';
import { AppDialogService } from './app-dialog.service';
import { LogService } from './log.service';
import { IUserList, IUserListRequest, IUserListResponse } from '../models/user.model';
import { TtlRequestCache } from '@utils/ttl-request-cache';


const userDetailsCacheBuster$ = new Subject<void>();


@Injectable()
export class UserService {
  private readonly apiURL: string = 'user';

  public selectedUser$: BehaviorSubject<IUserPage> =
    new BehaviorSubject<IUserPage>(null);

  // Backs getUser() below - see the comment there for why this exists and why it is not
  // @Cacheable. Cleared by the same userDetailsCacheBuster$ every existing save/update method in
  // this file already fires, and by globalCacheBusterNotifier because whether this user is
  // visible at all is decided against the caller's CSO (UserService.IsUserInScope) - a switch
  // must not leave either the payload or a UserNotInScope error answering for the old one.
  private readonly userCache = new TtlRequestCache<number, IUserPage>(
    null,
    merge(userDetailsCacheBuster$, globalCacheBusterNotifier),
  );

  constructor(
    private restClient: DSRESTClient,
    private logger: LogService,
    private _ads: AppDialogService,
    private _translate: TranslateService,
    private snackBar: MatSnackBar,
  ) {}

  get isUserSelectedLoading(): boolean {
    return this.selectedUser$.value === null;
  }

  startImpersonation(userId: number): Observable<void> {
    return this.restClient.postForResponse<void>(`impersonation/start/${userId}`, {});
  }

  stopImpersonation(): Observable<void> {
    return this.restClient.postForResponse<void>('impersonation/stop', {});
  }

  getUserList(): Observable<IUserList[]> {
    //console.log('getUserList ========================================================', `${this.apiURL}/getUserList/`);
    const apiResponse = this.restClient.getAsModel<IUserList[]>(`${this.apiURL}/GetUserList/`);
    return apiResponse.pipe(
      tap((response) => this.logger.log('User/GetUserList Response', response)),
    );
  }

  getUserListAllOrgs(): Observable<IUserList[]> {
    //console.log('getUserListAllOrgs ========================================================', `${this.apiURL}/GetUserListAllOrgs/`);
    const apiResponse = this.restClient.getAsModel<IUserList[]>(
      `${this.apiURL}/GetUserListAllOrgs/`,
    );
    return apiResponse.pipe(
      tap((response) =>
        this.logger.log('User/GetUserListAllOrgs Response', response),
      ),
    );
  }

  @Cacheable()
  getUserDropdownList(
    orgId?: number,
    includeChildOrgs: boolean = true,
    includeGroups: boolean = true,
    searchTerm?: string,
  ): Observable<UserDropdownDTO[]> {
    let url = `${this.apiURL}/users-dropdown-list${
      orgId ? `/${orgId}` : ''
    }?includeChildOrgs=${includeChildOrgs}&includeGroups=${includeGroups}`;
    if (searchTerm != null && searchTerm !== '')
      url += `&searchTerm=${encodeURIComponent(searchTerm)}`;
    const apiResponse = this.restClient.getAsModel<UserDropdownDTO[]>(url);
    return apiResponse.pipe(
      tap((response) =>
        this.logger.log('User/users-dropdown-list Response', response),
      ),
    );
  }

  // Backed by userCache (TtlRequestCache, not @Cacheable, so the cache is inspectable and
  // clearable from code) so the page-title resolver and the user-details pages share one request.
  // It holds no TTL, so its two busters are the only thing that ever evicts an entry - see the
  // field declaration.
  getUser(userId: number): Observable<IUserPage> {
    return this.userCache.get(userId, () => {
      const apiResponse = this.restClient.getDirect<IUserPage>(
        `${this.apiURL}/${userId}`,
      );

      //clear an existing user data while we fecth this user's data
      this.selectedUser$.next(null);
      return apiResponse.pipe(
        tap((response: IUserPage) => {
          this.logger.log('User/GetUser Response', response);
          this.selectedUser$.next(response); //store locally for use in other components (example: User Details Header)
        }),
        map((response: IUserPage) => {
          return this.mapResponseToUserPageVM(response);
        }),
      );
    });
  }

  private mapResponseToUserPageVM(response: IUserPage): IUserPage {
    if (
      response.userPermissions?.securities &&
      response.userPermissions.securities.length > 0
    ) {
      response.userPermissions.securities =
        response.userPermissions.securities.map((security) =>
          this.mapSecurityEntityTypeKeysToEnum(security),
        );
    }

    if (
      response.userPermissions?.userGroupPermissions &&
      response.userPermissions.userGroupPermissions.length > 0
    ) {
      response.userPermissions.userGroupPermissions =
        response.userPermissions.userGroupPermissions.map(
          (userGroupPermission) => ({
            ...userGroupPermission,
            securities: userGroupPermission.securities.map((security) =>
              this.mapSecurityEntityTypeKeysToEnum(security),
            ),
          }),
        );
    }

    return response;
  }

  private mapSecurityEntityTypeKeysToEnum(
    security: ISecurityRec,
  ): ISecurityRec {
    security.entityType =
      security.entityType === 'Group'
        ? EntityTypes.Group
        : security.entityType === 'Organization'
        ? EntityTypes.Organization
        : security.entityType === 'Location'
        ? EntityTypes.Location
        : security.entityType === 'Tank'
        ? EntityTypes.Tank
        : null;
    if (security?.permissions && security.permissions.length > 0) {
      security.permissions = security.permissions.map((permission) =>
        this.mapSecurityEntityTypeKeysToEnum(permission),
      );
    }
    return security;
  }

  getMyAccount(): Observable<IUserPage> {
    const apiResponse = this.restClient.getDirect<IUserPage>('my-account');

    return apiResponse.pipe(
      tap((response: IUserPage) => {
        this.logger.log('my-profile Response', response);
        this.selectedUser$.next(response); //store locally for use in other components (example: User Details Header)
      }),
    );
  }

  @CacheBuster({
    cacheBusterNotifier: userDetailsCacheBuster$,
    isInstant: true,
  })
  saveUser(user: any, isMyAccount: boolean): Observable<IUserPage> {
    globalCacheBusterNotifier.next();

    //map EntityTypes to the correct values
    user.userPermissions?.entityPermissionsGiven?.forEach((security) => {
      security.entityType =
        security.entityType === EntityTypes.Group
          ? 'Group'
          : security.entityType === EntityTypes.Organization
          ? 'Organization'
          : security.entityType === EntityTypes.Location
          ? 'Location'
          : security.entityType === EntityTypes.Tank
          ? 'Tank'
          : security.entityType;
    });

    const apiResponse = this.restClient.postForResponse<IUserPage>(
      `${this.apiURL}${isMyAccount ? '/my-account' : ''}`,
      user,
    );
    this._ads.showGeneralMessage(this._translate.instant('User.Form.Saving'));

    return apiResponse.pipe(
      tap((response) => this.logger.log('User/SaveUser Response', response)),
      tap({
        next: () => {
          this._ads.showSuccessMessage(
            this._translate.instant('User.Form.SaveSuccess'),
          );
        },
        error: () => {
          this._ads.showErrorMessage(
            this._translate.instant('User.Form.Failed'),
          );
        },
      }),
      map((response: IUserPage) => {
        return this.mapResponseToUserPageVM(response);
      }),
    );
  }

  // Admin Dashboard Privileges section (SBTMTAPWP-4467)
  getAdminDashboardPrivileges(
    userId: number,
  ): Observable<IAdminDashboardPrivileges> {
    const apiResponse = this.restClient.getDirect<IAdminDashboardPrivileges>(
      `${this.apiURL}/${userId}/admin-dashboard-privileges`,
    );
    return apiResponse.pipe(
      tap((response) =>
        this.logger.log('User/AdminDashboardPrivileges Response', response),
      ),
    );
  }

  saveAdminDashboardRoles(
    userId: number,
    roleNames: string[],
  ): Observable<IAdminDashboardPrivileges> {
    const apiResponse = this.restClient.postForResponse<IAdminDashboardPrivileges>(
      `${this.apiURL}/${userId}/admin-dashboard-privileges`,
      { roleNames },
    );
    return apiResponse.pipe(
      tap((response) =>
        this.logger.log('User/SaveAdminDashboardRoles Response', response),
      ),
    );
  }

  removePermission(entityGuid: any, userId: number): Observable<any> {
    const apiResponse = this.restClient.get(
      `${this.apiURL}/RemovePermission/${entityGuid}/${userId}`,
    );
    return apiResponse.pipe(
      tap((response) =>
        this.logger.log('Organization/RemovePermission Response', response),
      ),
    );
  }

  @CacheBuster({
    cacheBusterNotifier: userDetailsCacheBuster$,
    isInstant: true,
  })
  savePassword(
    userId: string | number,
    newPassword: string,
    isPendingPassword:boolean=false,
  ): Observable<{ errorMessages: string[] }> {
    if(!isPendingPassword){
      this._ads.showGeneralMessage(
        this._translate.instant('User.Password.Saving'),
      );
    }
    const apiResponse = this.restClient.post(
      `${this.apiURL}/${userId}/password`,
      { newPassword },
    );
    return apiResponse.pipe(
      tap((response) =>
        this.logger.log('User/save-user-password Response', response),
      ),
      tap({
        next: () => {
          if(!isPendingPassword){
            this._ads.showSuccessMessage(
              this._translate.instant('User.Password.SaveSuccess'),
            );
          }
        },
        error: () => {
          this._ads.showErrorMessage(
            this._translate.instant('User.Password.SaveFailed'),
          );
        },
      }),
    );
  }

  userNameAlreadyExists(userName: string): Observable<boolean> {
    const apiResponse = this.restClient.post(
      `${this.apiURL}/user-name-is-valid/`,
      {
        userName,
      },
    );
    return apiResponse.pipe(
      tap((response) =>
        this.logger.log('User/user-name-is-valid Response', response),
      ),
    );
  }

  @Cacheable()
  getAvailableGroups(userId: number): Observable<any> {
    const apiResponse = this.restClient.get(
      `${this.apiURL}/AvailableGroups/${userId}`,
    );
    return apiResponse.pipe(
      tap((response) =>
        this.logger.log('User/AvailableGroups Response', response),
      ),
    );
  }

  @Cacheable()
  getMemberGroups(userId: number): Observable<any> {
    const apiResponse = this.restClient.get(
      `${this.apiURL}/MemberGroups/${userId}`,
    );
    return apiResponse.pipe(
      tap((response) =>
        this.logger.log('User/MemberGroups Response', response),
      ),
    );
  }

  @Cacheable()
  getPrimaryContacts(userId: number): Observable<any> {
    const apiResponse = this.restClient.get(
      `${this.apiURL}/PrimaryContacts/${userId}`,
    );
    return apiResponse.pipe(
      tap((response) =>
        this.logger.log('User/PrimaryContacts Response', response),
      ),
    );
  }

  @Cacheable()
  getOrganizationUserWithGroupList(orgId: number): Observable<any> {
    const apiResponse = this.restClient.get(
      `${this.apiURL}/Org/${orgId}?includeGroupList=true`,
    );
    return apiResponse.pipe(
      tap((response) =>
        this.logger.log(`${this.apiURL}/Org/${orgId}`, response),
      ),
    );
  }

  @CacheBuster({
    cacheBusterNotifier: userDetailsCacheBuster$,
    isInstant: true,
  })
  resetPasswordAndNotifyUser(userId: number): Observable<any> {
    const apiResponse = this.restClient.get(
      `${this.apiURL}/${userId}/reset-password`,
    );
    return apiResponse.pipe(
      tap((response) =>
        this.logger.log('User/ResetPasswordAndNotifyUser Response', response),
      ),
    );
  }

  private showErrorMessage(message: string, response: any) {
    this.logger.log('User Error: ' + message, response);
    let duration = environment.snackbarDelayBeforeClose;

    this.snackBar.open(message, null, {
      duration: duration,
      panelClass: ['snackbar-failure'],
    });
  }

  @CacheBuster({
    cacheBusterNotifier: userDetailsCacheBuster$,
    isInstant: true,
  })
  getUserReports(userId: number): Observable<any> {
    const apiResponse = this.restClient.get(
      `${this.apiURL}/${userId}/userreports`,
    );
    return apiResponse.pipe(
      tap((response) =>
        this.logger.log('User/UserReports Response', response),
      ),
    );
  }

  @CacheBuster({
    cacheBusterNotifier: userDetailsCacheBuster$,
    isInstant: true,
  })
  getUserAlarms(userId: number): Observable<any> {
    const apiResponse = this.restClient.get(
      `${this.apiURL}/${userId}/useralarms`,
    );
    return apiResponse.pipe(
      tap((response) =>
        this.logger.log('User/UserReports Response', response),
      ),
    );
  }

  @CacheBuster({
    cacheBusterNotifier: userDetailsCacheBuster$,
    isInstant: true,
  })
  updateUserReportsRecipient(userId: number, reportsData : Map<number, [boolean, boolean, number]>): Observable<any> {
    const plainObject = Object.fromEntries(
      Array.from(reportsData.entries()).map(([key, value]) => [
        key,
        { isActive: value[0], isUserInGroup: value[1], groupId: value[2]} // To Match C# class
      ])
    );
    const apiResponse = this.restClient.post(
      `${this.apiURL}/${userId}/update-userreports-recipients`, plainObject
    );
     this._ads.showGeneralMessage(this._translate.instant('User.Tabs.UserReportsAndAlarms.Saving'));

    return apiResponse.pipe(
      tap((response) => this.logger.log('User/Update Userreports Recipients Response', response)),
      tap({
        next: () => {
          this._ads.showSuccessMessage(
            this._translate.instant('User.Tabs.UserReports.SaveSuccess'),
          );
        },
        error: () => {
          this._ads.showErrorMessage(
            this._translate.instant('User.Tabs.UserReportsAndAlarms.SaveFail'),
          );
        },
      }),
    );
  }

  @CacheBuster({
    cacheBusterNotifier: userDetailsCacheBuster$,
    isInstant: true,
  })
  updateUserAlarmsRecipient(userId: number, reportsData : Map<number, [boolean, boolean, number]>): Observable<any> {
    const plainObject = Object.fromEntries(
      Array.from(reportsData.entries()).map(([key, value]) => [
        key,
        { isActive: value[0], isUserInGroup: value[1], AlarmUserId: value[2] } // To Match C# class
      ])
    );
    const apiResponse = this.restClient.post(
      `${this.apiURL}/${userId}/update-useralarms-recipients`, plainObject
    );
     this._ads.showGeneralMessage(this._translate.instant('User.Tabs.UserReportsAndAlarms.Saving'));
    return apiResponse.pipe(
      tap((response) => this.logger.log('User/Update Useralarms Recipients Response', response)),
      tap({
        next: () => {
          this._ads.showSuccessMessage(
            this._translate.instant('User.Tabs.UserAlarms.SaveSuccess'),
          );
        },
        error: () => {
          this._ads.showErrorMessage(
            this._translate.instant('User.Tabs.UserReportsAndAlarms.SaveFail'),
          );
        },
      }),
    );
  }

  getUserListPaginated(request: IUserListRequest): Observable<IUserListResponse> {
    const apiResponse = this.restClient.post(
      `${this.apiURL}/GetUserListPaginated`,
      request
    );
    return apiResponse.pipe(
      map(response => response as IUserListResponse),
      tap((response) => this.logger.log('User/GetUserListPaginated Response', response))
    );
  }

  exportToExcel(request?: IUserListRequest): Observable<Blob> {
    return this.restClient.postFile(
      `${this.apiURL}/ExportToExcel`,
      request || {}
    );
  }

  @CacheBuster({
    cacheBusterNotifier: userDetailsCacheBuster$,
    isInstant: true,
  })
  getUserOwnedTankProfiles(userId: number): Observable<any> {
    const apiResponse = this.restClient.get(
      `${this.apiURL}/${userId}/usertankprofiles`,
    );
    return apiResponse.pipe(
      tap((response) =>
        this.logger.log('User/usertankprofiles Response', response),
      ),
    );
  }

  @CacheBuster({
    cacheBusterNotifier: userDetailsCacheBuster$,
    isInstant: true,
  })
  getUserOwnedFilterProfiles(userId: number): Observable<any> {
    const apiResponse = this.restClient.get(
      `${this.apiURL}/${userId}/userfilterprofiles`,
    );
    return apiResponse.pipe(
      tap((response) =>
        this.logger.log('User/userfilterprofiles Response', response),
      ),
    );
  }

  @CacheBuster({
    cacheBusterNotifier: userDetailsCacheBuster$,
    isInstant: true,
  })
  getUserReportsbyProfile(userId: number, profileId : number, profiletype : string): Observable<any> {
    const apiResponse = this.restClient.get(
      `${this.apiURL}/${userId}/userreportsbyprofileId?profileId=${profileId}&profile=${profiletype}`,
    );
    return apiResponse.pipe(
      tap((response) =>
        this.logger.log('User/UserReportsByProfile Response', response),
      ),
    );
  }

}
